<template>
  <div class="eg-radio-checkbox-input" :class="computedClass">
    <label
      v-for="(option, index) in options"
      :key="`${name}-${index}`"
      class="eg-radio-checkbox-input__label"
      :class="{
        'eg-radio-checkbox-input__label--disabled': disabled,
      }"
    >
      <input
        :id="`${name}-${index}`"
        :ref="`input-${index}`"
        :name="name"
        :value="option.value"
        :checked="isChecked(option.value)"
        :disabled="disabled"
        :type="type"
        class="eg-radio-checkbox-input__input"
        @change="handleChange"
      />
      <span v-if="option.label" class="eg-radio-checkbox-input__text">{{
        option.label
      }}</span>
      <slot
        v-if="$scopedSlots['content']"
        name="content"
        :option="option"
        :index="index"
      />
    </label>
  </div>
</template>

<script>
export default {
  name: "EgRadioCheckboxInput",
  props: {
    name: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array, Boolean],
      default: null,
    },
    options: {
      type: Array,
      required: true,
      validator: (options) =>
        options.every(
          (option) =>
            option &&
            typeof option === "object" &&
            "label" in option &&
            "value" in option
        ),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    customClass: {
      type: [String, Array, Object],
      default: null,
    },

    vertical: {
      type: Boolean,
      default: false,
    },

    type: {
      type: String,
      required: true,
      validator: (type) => ["radio", "checkbox"].includes(type),
    },
    valueIsId: {
      type: Boolean,
      default: false,
    },
    indeterminate: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "medium",
      validator: (v) => ["medium", "large"].includes(v),
    },
  },
  emits: ["change"],
  computed: {
    computedClass() {
      let customClass = this.customClass || {};

      if (typeof this.customClass === "string") {
        customClass = { [this.customClass]: true };
      }

      if (Array.isArray(this.customClass)) {
        customClass = this.customClass.reduce(
          (acc, cls) => ({ ...acc, [cls]: true }),
          {}
        );
      }

      return {
        ...customClass,
        "eg-radio-checkbox-input--vertical": this.vertical,
        "eg-radio-checkbox-input--radio": this.type === "radio",
        "eg-radio-checkbox-input--checkbox": this.type === "checkbox",
        [`eg-radio-checkbox-input--${this.size}`]: true,
      };
    },
  },
  watch: {
    indeterminate(newVal) {
      this.updateIndeterminateState(newVal);
    },
  },
  mounted() {
    this.updateIndeterminateState(this.indeterminate);
  },
  methods: {
    updateIndeterminateState(indeterminate) {
      if (this.type === "checkbox") {
        this.options.forEach((option, index) => {
          const inputRef = this.$refs[`input-${index}`];
          if (inputRef && inputRef.length) {
            inputRef[0].indeterminate = indeterminate;
          } else if (inputRef) {
            inputRef.indeterminate = indeterminate;
          }
        });
      }
    },
    isChecked(optionValue) {
      if (Array.isArray(this.value) && this.value.length > 0) {
        return this.value.find((item) =>
          this.valueIsId ? item.id === optionValue : item === optionValue
        );
      }

      return this.valueIsId && this.value?.id
        ? this.value.id === optionValue
        : this.value === optionValue;
    },
    handleChange(event) {
      if (this.disabled) {
        return;
      }

      if (this.type === "radio") {
        this.$emit("change", event.target.value);

        return;
      }

      const checkedInputs = this.$el.querySelectorAll("input:checked");
      this.$emit(
        "change",
        [...checkedInputs].map((input) => input.value)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.eg-radio-checkbox-input {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  &--vertical {
    flex-direction: column;
    flex-wrap: nowrap;
  }

  &__label {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: var(--typography-font-size-fs-300);
    line-height: 1.4;
    transition: color 0.2s ease;
    gap: 8px;
    &:hover:not(.eg-radio-checkbox-input__label--disabled) {
      color: var(--color-text-primary-hover, #333);
    }

    &--disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  &--large &__input {
    width: 20px;
    height: 20px;
  }

  &__input {
    appearance: none;
    width: 16px;
    height: 16px;
    border: 1px solid var(--color-surface-neutral-container-5);
    border-radius: 50%;
    background-color: transparent;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;
    // Inner dot via ::before
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 13px;
      height: 13px;
      border-radius: 50%;
      background-color: var(--color-surface-primary-default);
      transform: translate(-50%, -50%) scale(0);
      transition: transform 0.2s ease;
    }

    // Selected state
    &:checked {
      border-color: var(--color-stroke-primary-default);
      background-color: white; // White ring

      &::before {
        transform: translate(-50%, -50%) scale(1); // Inner dot appears
      }
    }

    // Disabled state
    &:disabled {
      border-color: var(--color-surface-neutral-container-5);
      background-color: var(--color-surface-neutral-container-5);
      cursor: not-allowed;

      &:checked::before {
        background-color: var(--color-surface-neutral-container-5);
        transform: translate(-50%, -50%) scale(1);
      }
    }
  }

  &__text {
    user-select: none;
  }

  &--checkbox {
    & .eg-radio-checkbox-input__input {
      border-radius: 4px;

      // Inner dot via ::before
      &::before {
        content: unset;
        background-color: transparent;
      }

      // Selected state
      &:checked {
        background: var(--color-surface-primary-default, #8a3ffc);

        &::before {
          content: "";
          transform: translate(-50%, -50%) scale(1); // Inner dot appears
          background-image: url("data:image/svg+xml,%3Csvg%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20clip-path%3D%22url(%23clip0_40010750_11460)%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M10.2652%202.73483C10.4116%202.88128%2010.4116%203.11872%2010.2652%203.26517L4.76517%208.76516C4.61872%208.91161%204.38128%208.91161%204.23483%208.76516L1.73483%206.26517C1.58839%206.11872%201.58839%205.88128%201.73483%205.73483C1.88128%205.58839%202.11872%205.58839%202.26517%205.73483L4.5%207.96967L9.73483%202.73483C9.88128%202.58839%2010.1187%202.58839%2010.2652%202.73483Z%22%20fill%3D%22white%22/%3E%3C/g%3E%3Cdefs%3E%3CclipPath%20id%3D%22clip0_40010750_11460%22%3E%3Crect%20width%3D%2212%22%20height%3D%2212%22%20fill%3D%22white%22/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
          background-size: contain;
        }
      }

      // Indeterminate state
      &:indeterminate {
        background: var(--color-surface-primary-default, #8a3ffc);
        border-color: var(--color-stroke-primary-default);

        &::before {
          content: "";
          width: 8px;
          height: 1px;
          border-radius: 0;
          transform: translate(-50%, -50%) scale(1);
          background-color: #fff;
          background-image: none;
        }
      }

      // Disabled state
      &:disabled {
        border-color: var(--color-surface-neutral-container-5);
        background-color: var(--color-surface-neutral-container-5);
        cursor: not-allowed;

        &:checked::before {
          background-color: var(--color-surface-neutral-container-5);
          transform: translate(-50%, -50%) scale(1);
        }

        &:indeterminate::before {
          background-color: var(--color-surface-neutral-container-5);
          transform: translate(-50%, -50%) scale(1);
        }
      }
    }
  }
}
</style>
